/**
 * Post-export validation: inspect generated .docx (OOXML) for font family and size on runs.
 * Does not re-open Word; reads the zip package in the browser.
 */

import JSZip from 'jszip';

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

// Word can emit extremely small half-point sizes (e.g. 1 = 0.5pt) for internal markers/fields.
// These are not user-authored typography and should not fail Draft 1.0 §4.1 compliance.
const IGNORED_HALF_POINT_SIZES = new Set([1]);

function normalizeFontToken(raw) {
  if (raw == null) return '';
  return String(raw)
    .replace(/["']/g, '')
    .split(',')[0]
    .trim()
    .toLowerCase();
}

function isThemeOrMajorMinorFont(token) {
  if (!token) return true;
  return /^\+/.test(token) || token === 'minorhansi' || token === 'minorascii';
}

function halfPointsFromConfig(fontConfig) {
  const cfg = fontConfig || {};
  const set = new Set();
  if (cfg && typeof cfg === 'object') {
    for (const k of Object.keys(cfg)) {
      const pt = Number(cfg[k]?.size);
      if (Number.isFinite(pt)) {
        set.add(Math.round(pt * 2));
      }
    }
  }
  // Draft 1.0 PDF §4: TOC styles (C-TOC Title 14 pt, C-TOC entries 11 pt), §4.2 minimum table 9 pt
  set.add(36); // 18 pt
  set.add(28); // 14 pt
  set.add(24); // 12 pt
  set.add(22); // 11 pt
  set.add(20); // 10 pt
  set.add(18); // 9 pt
  return set;
}

function attr(el, name) {
  if (!el) return null;
  return el.getAttribute(`w:${name}`) ?? el.getAttribute(name);
}

function collectPartNames(zip) {
  return Object.keys(zip.files).filter((n) => {
    if (zip.files[n].dir) return false;
    if (n === 'word/document.xml') return true;
    if (n === 'word/footnotes.xml') return true;
    if (n === 'word/endnotes.xml') return true;
    if (/^word\/header\d+\.xml$/.test(n)) return true;
    if (/^word\/footer\d+\.xml$/.test(n)) return true;
    return false;
  });
}

function scanRunsInXml(xml, partLabel, allowedHalf, requiredFamilySubstr, invalidFonts, invalidSizes) {
  if (!xml || typeof xml !== 'string') return;
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length) return;

  const runs = dom.getElementsByTagNameNS(W_NS, 'r');
  for (let i = 0; i < runs.length; i += 1) {
    const r = runs[i];
    const rPr = r.getElementsByTagNameNS(W_NS, 'rPr')[0];
    if (!rPr) continue;

    const rFonts = rPr.getElementsByTagNameNS(W_NS, 'rFonts')[0];
    if (rFonts) {
      const ascii = normalizeFontToken(attr(rFonts, 'ascii'));
      const hAnsi = normalizeFontToken(attr(rFonts, 'hAnsi'));
      const eastAsia = normalizeFontToken(attr(rFonts, 'eastAsia'));
      const cs = normalizeFontToken(attr(rFonts, 'cs'));

      const candidates = [ascii, hAnsi, eastAsia, cs].filter(Boolean);
      const meaningful = candidates.filter((t) => !isThemeOrMajorMinorFont(t));
      if (meaningful.length) {
        const ok = meaningful.every((t) => t.includes(requiredFamilySubstr));
        if (!ok) {
          invalidFonts.add(
            `[${partLabel}] rFonts ascii/hAnsi/… → ${meaningful.join(' / ')} (expected ${requiredFamilySubstr})`,
          );
        }
      }
    }

    const checkSz = (el, tag) => {
      const val = attr(el, 'val');
      if (val == null) return;
      const half = parseInt(val, 10);
      if (!Number.isFinite(half)) return;
      if (IGNORED_HALF_POINT_SIZES.has(half)) return;
      if (!allowedHalf.has(half)) {
        invalidSizes.add(
          `[${partLabel}] ${tag} w:val="${half}" (${(half / 2).toFixed(1)} pt) not in FONT_CONFIG allowlist`,
        );
      }
    };

    const szEls = rPr.getElementsByTagNameNS(W_NS, 'sz');
    for (let s = 0; s < szEls.length; s += 1) {
      checkSz(szEls[s], 'w:sz');
    }
    const szCsEls = rPr.getElementsByTagNameNS(W_NS, 'szCs');
    for (let s = 0; s < szCsEls.length; s += 1) {
      checkSz(szCsEls[s], 'w:szCs');
    }
  }
}

/**
 * @param {Blob} blob - Generated .docx
 * @param {object} fontConfig - Same shape as DocumentEditor FONT_CONFIG (sizes in points)
 * @returns {Promise<{ ok: boolean, errors: string[], partsScanned: number }>}
 */
export async function validateExportedDocxBlob(blob, fontConfig) {
  if (!blob || typeof blob.arrayBuffer !== 'function') {
    return { ok: false, errors: ['Invalid blob'], partsScanned: 0 };
  }

  let zip;
  try {
    const buf = await blob.arrayBuffer();
    zip = await JSZip.loadAsync(buf);
  } catch (e) {
    return {
      ok: false,
      errors: [`Could not read DOCX as zip: ${e?.message || String(e)}`],
      partsScanned: 0,
    };
  }

  const requiredFamilySubstr = normalizeFontToken(fontConfig?.body?.name || 'Times New Roman');
  const allowedHalf = halfPointsFromConfig(fontConfig);
  const invalidFonts = new Set();
  const invalidSizes = new Set();

  const names = collectPartNames(zip);
  let scanned = 0;
  const readErrors = [];
  for (const name of names) {
    const file = zip.file(name);
    if (!file) continue;
    try {
      const xml = await file.async('string');
      scanRunsInXml(xml, name, allowedHalf, requiredFamilySubstr, invalidFonts, invalidSizes);
      scanned += 1;
    } catch (e) {
      readErrors.push(`Failed to read part ${name}: ${e?.message || String(e)}`);
    }
  }

  if (scanned === 0) {
    readErrors.push('No WordprocessingML parts found (word/document.xml missing?)');
  }

  const fontList = Array.from(invalidFonts);
  const sizeList = Array.from(invalidSizes);
  const findings = [
    ...readErrors,
    ...fontList.slice(0, 50),
    ...sizeList.slice(0, 50),
  ];
  if (fontList.length > 50) {
    findings.push(`… and ${fontList.length - 50} more font-family issue(s)`);
  }
  if (sizeList.length > 50) {
    findings.push(`… and ${sizeList.length - 50} more font-size issue(s)`);
  }

  return { ok: findings.length === 0, errors: findings, partsScanned: scanned };
}
